import DOMPurify from 'dompurify';
import { useCallback } from 'react';

/**
 * URI schemes permitted by DOMPurify in sanitized HTML.
 *
 * Allowed schemes:
 *   http, https, ftp, ftps, mailto, tel, callto, sms, cid, xmpp, matrix, file
 *
 * Special cases:
 *   - data:image/<subtype>;base64,  — only base64-encoded images (e.g.
 *     "data:image/png;base64,iVBOR..."), not arbitrary data: URIs
 *   - file: — for offline cached images rendered as <img src="file://...">.
 *     Post-processing below strips file: from <a href> to prevent clickable
 *     file:// links.
 *
 * The trailing alternations handle relative URIs and scheme-relative URLs
 * (e.g. "//example.com" or "/path") per DOMPurify's default pattern.
 */
const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|file):|data:image\/[a-z0-9.+-]+;base64,|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

const useDOMPurify = () => {
  const sanitize = useCallback((html: string) => {
    const sanitizedHtml = DOMPurify.sanitize(html, {
      ALLOWED_URI_REGEXP,
    });

    const container = document.createElement('div');
    container.innerHTML = sanitizedHtml;

    // file: is allowed in ALLOWED_URI_REGEXP for <img src="file://...">
    // (offline cached images) but must not leak into other elements.
    // Strip file: from src on non-img elements, and href on any element.
    container.querySelectorAll('[src^="file:" i]:not(img)').forEach((el) => {
      el.removeAttribute('src');
    });
    container.querySelectorAll('[href^="file:" i]').forEach((el) => {
      el.removeAttribute('href');
    });

    const links = container.querySelectorAll('a');
    links.forEach((link) => {
      link.setAttribute('target', '_blank');

      const existingRel = link.getAttribute('rel');
      const relTokens = new Set((existingRel ?? '').split(/\s+/).filter((token) => token.length > 0));
      relTokens.add('noopener');
      relTokens.add('noreferrer');
      link.setAttribute('rel', Array.from(relTokens).join(' '));
    });

    return container.innerHTML;
  }, []);

  return { sanitize };
};

export default useDOMPurify;
